import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { Tabs, TabsList, TabsTrigger } from '@workspace/ui/components/tabs'
import { formatDuration } from '@workspace/ui/lib/utils'
import { ArrowLeft, ChevronRight, FileText, Loader2, RefreshCw, Trash2, Video } from 'lucide-react'
import { useState } from 'react'
import { MarkdownContent } from '@/components/markdown-content'
import { QueryGuard } from '@/components/query-guard'
import { PitchSessionDetail } from '@/features/pitch/components/pitch-session-detail'
import { VideoPreviewModal } from '@/features/pitch/components/video-preview-modal'
import { DocumentPreviewModal, type PreviewDocument } from '@/features/preview'
import {
  useAdminDeleteEvaluationMutation,
  useAdminDeletePitchSessionMutation,
  useAdminEvaluationByIdQuery,
  useAdminInvestorsQuery,
  useAdminRegenerateEvaluationMutation,
  useGetPitchVideoUrlMutation,
} from '@/hooks/api'
import { formatDateTime } from '@/utils/format'

export const Route = createFileRoute('/_protected/admin/evaluations/$id')({
  component: AdminEvaluationDetailPage,
})

type Tab = 'overview' | 'deep-research' | 'details'

function AdminEvaluationDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const evaluationQuery = useAdminEvaluationByIdQuery(id)
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)
  const getVideoUrlMutation = useGetPitchVideoUrlMutation({
    onSuccess: (data) => setVideoUrl(data.data.url),
  })

  const [deletePitchDialogOpen, setDeletePitchDialogOpen] = useState(false)
  const deletePitchSessionMutation = useAdminDeletePitchSessionMutation({
    onSuccess: () => {
      navigate({ to: '/admin/evaluations' })
    },
  })

  const [deleteEvalDialogOpen, setDeleteEvalDialogOpen] = useState(false)
  const deleteEvaluationMutation = useAdminDeleteEvaluationMutation({
    onSuccess: () => {
      setDeleteEvalDialogOpen(false)
      setSelectedEvaluationId(null)
      evaluationQuery.refetch()
    },
  })

  const [runDialogOpen, setRunDialogOpen] = useState(false)
  const [selectedInvestorId, setSelectedInvestorId] = useState<string | undefined>(undefined)
  const regenerateMutation = useAdminRegenerateEvaluationMutation({
    onSuccess: () => {
      setRunDialogOpen(false)
      setSelectedInvestorId(undefined)
    },
  })
  const investorsQuery = useAdminInvestorsQuery(
    { query: { hasPersona: true, limit: 100 } },
    { enabled: runDialogOpen }
  )

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'deep-research', label: 'Deep Research' },
    { id: 'details', label: 'Details' },
  ]

  return (
    <QueryGuard query={evaluationQuery} errorMessage="Failed to load evaluation">
      {(pitchSession) => {
        const allEvaluations = pitchSession.evaluations
        const activeEvaluationId = selectedEvaluationId ?? allEvaluations[0]?.id ?? null
        const selectedEvaluation = allEvaluations.find((e) => e.id === activeEvaluationId) ?? null
        const isCompleted = pitchSession.status === 'COMPLETED'

        return (
          <>
            <nav className="border-b border-border">
              <div className="flex flex-wrap items-center gap-2 py-3">
                <Link
                  to="/admin/evaluations"
                  className="flex items-center gap-1 text-sm font-medium text-[#f97316] hover:underline"
                >
                  <ArrowLeft className="size-4" />
                  Evaluations
                </Link>
                <ChevronRight className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-medium">
                  {pitchSession.startupName ?? 'Unknown Startup'}
                </h2>
                {pitchSession.investorName && (
                  <>
                    <span className="text-sm text-muted-foreground">/</span>
                    <span className="text-sm text-muted-foreground">
                      {pitchSession.investorName}
                    </span>
                  </>
                )}
                <Dialog open={deletePitchDialogOpen} onOpenChange={setDeletePitchDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="ml-auto text-destructive">
                      <Trash2 className="mr-1.5 size-4" />
                      Delete Pitch Session
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Pitch Session</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        This will permanently delete this pitch session and all its evaluations.
                        This action cannot be undone.
                      </p>
                      <Button
                        variant="destructive"
                        className="w-full"
                        disabled={deletePitchSessionMutation.isPending}
                        onClick={() =>
                          deletePitchSessionMutation.mutate({ path: { id: pitchSession.id } })
                        }
                      >
                        {deletePitchSessionMutation.isPending ? (
                          <Loader2 className="mr-1.5 size-4 animate-spin" />
                        ) : null}
                        Delete Pitch Session
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="flex gap-1 -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 text-sm font-medium border-b-2 ${
                      activeTab === tab.id
                        ? 'border-[#f97316] text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>
            <div className="pb-4">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {isCompleted && (
                    <div className="flex items-center gap-4 overflow-hidden">
                      <Tabs
                        value={activeEvaluationId ?? undefined}
                        onValueChange={setSelectedEvaluationId}
                        className="min-w-0 flex-1 overflow-hidden"
                      >
                        <TabsList className="overflow-x-auto justify-start w-max max-w-full">
                          {allEvaluations.map((evaluation, index) => (
                            <TabsTrigger
                              key={evaluation.id}
                              value={evaluation.id}
                              className="gap-2 px-4"
                            >
                              <span>
                                #{allEvaluations.length - index}{' '}
                                {evaluation.personaName ?? 'Council'}
                              </span>
                              <span className="text-xs opacity-70">
                                {formatDateTime(evaluation.createdAt)}
                              </span>
                              {(evaluation.status === 'PENDING' ||
                                evaluation.status === 'PROCESSING') && (
                                <Loader2 className="size-3.5 animate-spin" />
                              )}
                              {evaluation.status === 'FAILED' && (
                                <span className="text-xs text-destructive">Failed</span>
                              )}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </Tabs>
                      <Dialog
                        open={runDialogOpen}
                        onOpenChange={(open) => {
                          setRunDialogOpen(open)
                          if (!open) setSelectedInvestorId(undefined)
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="shrink-0">
                            <RefreshCw className="mr-1.5 size-4" />
                            Run Evaluation
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Run Evaluation</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <p className="text-sm text-muted-foreground">
                                Select an investor to run a custom persona evaluation, or leave
                                empty to run the default council evaluation.
                              </p>
                              <Select
                                value={selectedInvestorId ?? 'council'}
                                onValueChange={(v) =>
                                  setSelectedInvestorId(v === 'council' ? undefined : v)
                                }
                                disabled={investorsQuery.isPending}
                              >
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={
                                      investorsQuery.isPending
                                        ? 'Loading investors...'
                                        : 'Council personas (default)'
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="council">Council (default)</SelectItem>
                                  {investorsQuery.data?.data.items.map((investor) => (
                                    <SelectItem key={investor.id} value={investor.id}>
                                      {investor.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              className="w-full"
                              disabled={regenerateMutation.isPending}
                              onClick={() =>
                                regenerateMutation.mutate({
                                  path: { id },
                                  body: { investorId: selectedInvestorId },
                                })
                              }
                            >
                              {regenerateMutation.isPending ? (
                                <Loader2 className="mr-1.5 size-4 animate-spin" />
                              ) : null}
                              {selectedInvestorId
                                ? 'Run Custom Persona Evaluation'
                                : 'Run Council Evaluation'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      {activeEvaluationId && (
                        <Dialog open={deleteEvalDialogOpen} onOpenChange={setDeleteEvalDialogOpen}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 text-destructive"
                            >
                              <Trash2 className="mr-1.5 size-4" />
                              Delete Evaluation
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Delete Evaluation</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <p className="text-sm text-muted-foreground">
                                This will permanently delete this evaluation. This action cannot be
                                undone.
                              </p>
                              <Button
                                variant="destructive"
                                className="w-full"
                                disabled={deleteEvaluationMutation.isPending}
                                onClick={() =>
                                  deleteEvaluationMutation.mutate({
                                    path: { id: activeEvaluationId },
                                  })
                                }
                              >
                                {deleteEvaluationMutation.isPending ? (
                                  <Loader2 className="mr-1.5 size-4 animate-spin" />
                                ) : null}
                                Delete Evaluation
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  )}
                  <PitchSessionDetail
                    status={pitchSession.status}
                    pitchId={pitchSession.id}
                    videoKey={pitchSession.videoKey}
                    videoCoverImageUrl={pitchSession.videoCoverImageUrl}
                    pitchDeckId={pitchSession.pitchDeckId}
                    evaluation={selectedEvaluation}
                    createdAt={pitchSession.createdAt}
                    finishedAt={pitchSession.finishedAt}
                    onBack={() => window.history.back()}
                  />
                </div>
              )}
              {activeTab === 'deep-research' && (
                <div className="space-y-6">
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="mb-3 text-xs font-medium text-muted-foreground">Team Research</p>
                    {pitchSession.teamResearch ? (
                      <MarkdownContent>{pitchSession.teamResearch}</MarkdownContent>
                    ) : (
                      <p className="text-sm text-muted-foreground">No team research available.</p>
                    )}
                  </div>
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="mb-3 text-xs font-medium text-muted-foreground">
                      Market Research
                    </p>
                    {pitchSession.marketResearch ? (
                      <MarkdownContent>{pitchSession.marketResearch}</MarkdownContent>
                    ) : (
                      <p className="text-sm text-muted-foreground">No market research available.</p>
                    )}
                  </div>
                </div>
              )}
              {activeTab === 'details' && (
                <div className="space-y-6">
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pitchSession.videoKey}
                      onClick={() =>
                        pitchSession.videoKey &&
                        getVideoUrlMutation.mutate({ path: { pitchId: pitchSession.id } })
                      }
                    >
                      <Video className="mr-1.5 size-4" />
                      Recording
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pitchSession.pitchDeckId}
                      onClick={() =>
                        pitchSession.pitchDeckId &&
                        setPreviewDocument({
                          id: pitchSession.pitchDeckId,
                          fileName: 'Pitch Deck',
                          mimeType: 'application/pdf',
                        })
                      }
                    >
                      <FileText className="mr-1.5 size-4" />
                      Pitch Deck
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-xs text-muted-foreground">Created</p>
                      <p className="mt-1 text-sm font-medium">
                        {formatDateTime(pitchSession.createdAt)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-xs text-muted-foreground">Started</p>
                      <p className="mt-1 text-sm font-medium">
                        {formatDateTime(pitchSession.startedAt)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-xs text-muted-foreground">Finished</p>
                      <p className="mt-1 text-sm font-medium">
                        {formatDateTime(pitchSession.finishedAt)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-xs text-muted-foreground">Duration</p>
                      <p className="mt-1 text-sm font-medium">
                        {pitchSession.startedAt && pitchSession.finishedAt
                          ? formatDuration(
                              Math.floor(
                                (new Date(pitchSession.finishedAt).getTime() -
                                  new Date(pitchSession.startedAt).getTime()) /
                                  1000
                              )
                            )
                          : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="mb-3 text-xs font-medium text-muted-foreground">
                      Pitch Deck Content
                    </p>
                    {pitchSession.pitchDeckFullText ? (
                      <MarkdownContent>{pitchSession.pitchDeckFullText}</MarkdownContent>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No pitch deck content available.
                      </p>
                    )}
                  </div>
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="mb-3 text-xs font-medium text-muted-foreground">Questions</p>
                    {pitchSession.questions ? (
                      <MarkdownContent>{pitchSession.questions}</MarkdownContent>
                    ) : (
                      <p className="text-sm text-muted-foreground">No questions available.</p>
                    )}
                  </div>
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="mb-3 text-xs font-medium text-muted-foreground">Transcript</p>
                    {pitchSession.transcript ? (
                      <MarkdownContent>{pitchSession.transcript}</MarkdownContent>
                    ) : (
                      <p className="text-sm text-muted-foreground">No transcript available.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {videoUrl && (
              <VideoPreviewModal
                videoUrl={videoUrl}
                title="Pitch Recording"
                open
                onOpenChange={(open) => !open && setVideoUrl(null)}
              />
            )}
            {previewDocument && (
              <DocumentPreviewModal
                document={previewDocument}
                open
                onOpenChange={(open) => !open && setPreviewDocument(null)}
              />
            )}
          </>
        )
      }}
    </QueryGuard>
  )
}
